import React from "react";
import styled, { keyframes } from "styled-components";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import taller from "../Home/Images/tallerestanteria.jpeg";
import manos from "../Home/Images/clientapintando.jpeg";
import jarra from "../Home/Images/jarraconceramicos.jpeg";
import familia from "./images/tres-generaciones-peluche.png";

const reveal = keyframes`
  from { opacity: 0; transform: translateY(42px); }
  to { opacity: 1; transform: translateY(0); }
`;

const drift = keyframes`
  0%, 100% { transform: rotate(-3deg) translateY(0); }
  50% { transform: rotate(-1.5deg) translateY(-12px); }
`;

const marquee = keyframes`
  to { transform: translateX(-50%); }
`;

const Page = styled.main`
  --paper: #f3ecdf;
  --paper-light: #faf6ed;
  --forest: #173f35;
  --clay: #cf7046;
  --acid: #d9e7a8;
  --ink: #1e2824;
  overflow: hidden;
  background: var(--paper);
  color: var(--ink);
`;

const Hero = styled.section`
  min-height: min(920px, calc(100svh - 72px));
  position: relative;
  isolation: isolate;
  display: grid;
  align-items: end;
  padding: clamp(2.5rem, 7vw, 7rem) clamp(1rem, 4vw, 4rem) clamp(2rem, 5vw, 4.5rem);
  background:
    radial-gradient(circle at 82% 18%, rgba(207, 112, 70, 0.22), transparent 30%),
    radial-gradient(circle at 8% 70%, rgba(217, 231, 168, 0.48), transparent 26%),
    var(--paper-light);

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    z-index: -1;
    opacity: 0.24;
    pointer-events: none;
    background-image: radial-gradient(rgba(23, 63, 53, 0.28) 0.55px, transparent 0.55px);
    background-size: 4px 4px;
  }
`;

const HeroGrid = styled.div`
  width: min(100%, 1400px);
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(280px, 0.85fr);
  gap: clamp(2rem, 6vw, 7rem);
  align-items: end;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
    align-items: start;
    gap: 2.2rem;
  }
`;

const Intro = styled.div`
  position: relative;
  z-index: 3;
`;

const Eyebrow = styled.p`
  color: var(--forest);
  font-family: Arial, sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  animation: ${reveal} 0.75s both;
`;

const Title = styled.h1`
  max-width: 9.7ch;
  margin-top: 1rem;
  color: var(--forest);
  font-size: clamp(3.6rem, 9.2vw, 9rem);
  font-weight: 500;
  line-height: 0.82;
  letter-spacing: -0.065em;
  text-wrap: balance;
  animation: ${reveal} 0.9s 0.08s both;

  em {
    display: block;
    color: var(--clay);
    font-weight: 400;
  }
`;

const LeadRow = styled.div`
  margin-top: clamp(2rem, 5vw, 4.5rem);
  display: grid;
  grid-template-columns: 72px minmax(0, 34rem);
  gap: 1rem;
  align-items: start;
  animation: ${reveal} 0.9s 0.2s both;
`;

const LeadArrow = styled(ArrowDownRight)`
  width: 2.4rem;
  height: 2.4rem;
  color: var(--clay);
  flex-shrink: 0;
`;

const Lead = styled.p`
  color: #4b514d;
  font-size: clamp(1.08rem, 1.65vw, 1.42rem);
  line-height: 1.45;
  text-wrap: pretty;
`;

const Collage = styled.div`
  position: relative;
  min-height: clamp(350px, 52vw, 650px);
  animation: ${reveal} 1s 0.16s both;

  @media (max-width: 820px) {
    width: min(92%, 520px);
    min-height: 440px;
    margin-left: auto;
  }
`;

const MainPhoto = styled.figure`
  position: absolute;
  inset: 0 6% 6% 8%;
  overflow: hidden;
  border-radius: 48% 48% 9rem 9rem / 20% 20% 10rem 10rem;
  background: #d8c5a8;
  transform: rotate(2.5deg);

  img { width: 100%; height: 100%; object-fit: cover; }
`;

const DetailPhoto = styled.figure`
  position: absolute;
  left: -9%;
  bottom: 0;
  width: 44%;
  aspect-ratio: 0.82;
  padding: 0.5rem;
  background: var(--paper-light);
  box-shadow: 0 24px 55px rgba(35, 44, 39, 0.2);
  animation: ${drift} 7s ease-in-out infinite;

  img { width: 100%; height: 100%; object-fit: cover; }
`;

const Stamp = styled.div`
  position: absolute;
  right: -1rem;
  top: 10%;
  width: clamp(90px, 12vw, 140px);
  aspect-ratio: 1;
  display: grid;
  place-items: center;
  border: 1px solid rgba(23, 63, 53, 0.5);
  border-radius: 50%;
  color: var(--forest);
  background: var(--acid);
  font-family: Arial, sans-serif;
  font-size: clamp(0.62rem, 1vw, 0.78rem);
  font-weight: 700;
  line-height: 1.4;
  letter-spacing: 0.12em;
  text-align: center;
  text-transform: uppercase;
  transform: rotate(9deg);
`;

const Marquee = styled.div`
  overflow: hidden;
  padding: 1rem 0 0.9rem;
  border-top: 1px solid rgba(23, 63, 53, 0.28);
  border-bottom: 1px solid rgba(23, 63, 53, 0.28);
  background: var(--acid);
`;

const MarqueeTrack = styled.div`
  width: max-content;
  display: flex;
  animation: ${marquee} 32s linear infinite;
  will-change: transform;

  span {
    color: var(--forest);
    font-size: clamp(1rem, 1.7vw, 1.35rem);
    font-style: italic;
    white-space: nowrap;
  }
`;

const Manifesto = styled.section`
  width: min(100%, 1400px);
  margin: 0 auto;
  padding: clamp(5rem, 12vw, 11rem) clamp(1rem, 5vw, 5rem);

  @media (max-width: 1100px) {
    padding-top: 4.5rem;
    padding-bottom: 5.5rem;
  }
`;

const Number = styled.span`
  display: block;
  margin-bottom: 1.8rem;
  color: var(--clay);
  font-family: Arial, sans-serif;
  font-size: 0.75rem;
  letter-spacing: 0.15em;
`;

const Statement = styled.p`
  max-width: 18ch;
  margin-left: auto;
  color: var(--forest);
  font-size: clamp(2.3rem, 5.6vw, 5.8rem);
  line-height: 1.02;
  letter-spacing: -0.045em;
  text-wrap: balance;

  strong { color: var(--clay); font-weight: 500; }

  @media (max-width: 1100px) {
    max-width: 13ch;
    margin-left: 0;
  }

  @supports (animation-timeline: view()) {
    animation: ${reveal} both;
    animation-timeline: view();
    animation-range: entry 10% cover 38%;
  }
`;

const Story = styled.section`
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  min-height: 780px;
  background: var(--forest);

  @media (max-width: 760px) { grid-template-columns: 1fr; }
`;

const StickyImage = styled.div`
  position: relative;
  min-height: 600px;
  overflow: hidden;

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    filter: saturate(0.84) contrast(1.03);
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent 60%, rgba(23, 63, 53, 0.7));
  }
`;

const StoryCopy = styled.div`
  align-self: center;
  padding: clamp(4rem, 9vw, 9rem) clamp(1.3rem, 6vw, 7rem);
`;

const StoryKicker = styled(Eyebrow)`
  color: var(--acid);
  animation: none;
`;

const StoryTitle = styled.h2`
  margin-top: 1.5rem;
  color: #f7f0e5;
  font-size: clamp(2.7rem, 5vw, 5.5rem);
  font-weight: 400;
  line-height: 0.98;
`;

const StoryText = styled.p`
  max-width: 34rem;
  margin-top: 2rem;
  color: rgba(247, 240, 229, 0.76);
  font-family: Arial, sans-serif;
  font-size: clamp(1rem, 1.4vw, 1.2rem);
  line-height: 1.75;
`;

const Values = styled.section`
  width: min(100%, 1400px);
  margin: 0 auto;
  padding: clamp(5rem, 10vw, 9rem) clamp(1rem, 5vw, 5rem);
`;

const ValuesHead = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 2rem;
  align-items: end;
  margin-bottom: 3rem;
`;

const ValuesTitle = styled.h2`
  color: var(--forest);
  font-size: clamp(2.8rem, 6.5vw, 6.5rem);
  font-weight: 400;
  line-height: 0.9;
`;

const ValuesNote = styled.p`
  max-width: 20ch;
  color: #626860;
  font-family: Arial, sans-serif;
  font-size: 0.85rem;
  line-height: 1.5;
  text-align: right;

  @media (max-width: 620px) { display: none; }
`;

const Value = styled.div`
  display: grid;
  grid-template-columns: 72px minmax(170px, 0.8fr) minmax(0, 1fr);
  gap: clamp(1rem, 4vw, 4rem);
  align-items: baseline;
  padding: 1.55rem 0;
  border-top: 1px solid rgba(23, 63, 53, 0.28);
  transition: padding-left 0.35s ease, color 0.35s ease;

  &:last-child { border-bottom: 1px solid rgba(23, 63, 53, 0.28); }
  &:hover { padding-left: 1rem; }

  @media (max-width: 680px) {
    grid-template-columns: 36px 1fr;
    gap: 0.8rem;
  }
`;

const ValueNumber = styled.span`
  color: var(--clay);
  font-family: Arial, sans-serif;
  font-size: 0.72rem;
`;

const ValueTitle = styled.h3`
  color: var(--forest);
  font-size: clamp(1.55rem, 3vw, 2.8rem);
  font-weight: 400;
`;

const ValueText = styled.p`
  color: #626860;
  font-family: Arial, sans-serif;
  font-size: clamp(0.95rem, 1.3vw, 1.1rem);
  line-height: 1.6;

  @media (max-width: 680px) { grid-column: 2; }
`;

const Closing = styled.section`
  position: relative;
  min-height: min(800px, 82svh);
  display: grid;
  place-items: center;
  isolation: isolate;
  padding: 4rem 1rem;
  text-align: center;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    z-index: -2;
    background: url(${jarra}) center 53% / cover;
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    z-index: -1;
    background: linear-gradient(180deg, rgba(17, 42, 35, 0.35), rgba(17, 42, 35, 0.78));
  }
`;

const ClosingInner = styled.div`
  max-width: 930px;
`;

const ClosingTitle = styled.h2`
  color: #fff8ed;
  font-size: clamp(3rem, 8vw, 8rem);
  font-weight: 400;
  line-height: 0.88;
  letter-spacing: -0.055em;
  text-wrap: balance;
`;

const Actions = styled.div`
  margin-top: 2.5rem;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.8rem;
`;

const Action = styled(NavLink)`
  display: inline-flex;
  align-items: center;
  gap: 1.2rem;
  padding: 0.9rem 1.25rem;
  border: 1px solid rgba(255, 255, 255, 0.65);
  border-radius: 0;
  background: ${({ $primary }) => ($primary ? "#d9e7a8" : "rgba(17, 42, 35, 0.22)")};
  color: ${({ $primary }) => ($primary ? "#173f35" : "#fff")};
  font-family: Arial, sans-serif;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  backdrop-filter: blur(8px);
  transition: transform 0.3s ease, background 0.3s ease;
  &:hover { transform: translateY(-4px); background: #fff8ed; color: var(--forest); }
`;

const ActionArrow = styled(ArrowUpRight)`
  width: 1.1rem;
  height: 1.1rem;
  flex-shrink: 0;
`;

const QuienesSomos = () => {
  const { t } = useTranslation();
  const values = [
    [t("quienes.valor1Titulo"), t("quienes.valor1Texto")],
    [t("quienes.valor2Titulo"), t("quienes.valor2Texto")],
    [t("quienes.valor3Titulo"), t("quienes.valor3Texto")],
    [t("quienes.valor4Titulo"), t("quienes.valor4Texto")],
  ];
  const pulse = [t("quienes.pulso1"), t("quienes.pulso2"), t("quienes.pulso3"), t("quienes.pulso4")];
  const marqueeText = `${pulse.join("  ·  ")}  ·  `;

  return (
    <Page>
      <Helmet>
        <title>{t("quienes.titulo")} | El Taller de Aguaymanto</title>
        <meta name="description" content={t("quienes.deck")} />
      </Helmet>

      <Hero>
        <HeroGrid>
          <Intro>
            <Eyebrow>{t("quienes.kicker")} — El Taller de Aguaymanto</Eyebrow>
            <Title>Crear, conectar <em>y bajar el ritmo.</em></Title>
            <LeadRow><LeadArrow aria-hidden="true" /><Lead>{t("quienes.lead")}</Lead></LeadRow>
          </Intro>
          <Collage aria-label={t("quienes.titulo")}>
            <MainPhoto><img src={taller} alt="Interior de El Taller de Aguaymanto" /></MainPhoto>
            <DetailPhoto><img src={manos} alt="Pintando cerámica a mano" /></DetailPhoto>
            <Stamp>Vilanova<br />del Vallès<br />Barcelona</Stamp>
          </Collage>
        </HeroGrid>
      </Hero>

      <Marquee aria-hidden="true">
        <MarqueeTrack><span>{marqueeText.repeat(8)}</span><span>{marqueeText.repeat(8)}</span></MarqueeTrack>
      </Marquee>

      <Manifesto>
        <Number>01 — {t("quienes.bloque1Titulo")}</Number>
        <Statement>
          {t("quienes.linea1")} <strong>{t("quienes.linea3")}</strong>
        </Statement>
      </Manifesto>

      <Story>
        <StickyImage><img src={familia} alt="Abuela, madre y nieta pintando cerámica juntas" /></StickyImage>
        <StoryCopy>
          <StoryKicker>02 — {t("quienes.bloque2Titulo")}</StoryKicker>
          <StoryTitle>Tres generaciones unidas por el arte.</StoryTitle>
          <StoryText>{t("quienes.p3")} {t("quienes.linea2")}</StoryText>
        </StoryCopy>
      </Story>

      <Values>
        <ValuesHead>
          <ValuesTitle>{t("quienes.valoresTitulo")}</ValuesTitle>
          <ValuesNote>{t("quienes.deck")}</ValuesNote>
        </ValuesHead>
        {values.map(([title, text], index) => (
          <Value key={title}>
            <ValueNumber>0{index + 1}</ValueNumber>
            <ValueTitle>{title}</ValueTitle>
            <ValueText>{text}</ValueText>
          </Value>
        ))}
      </Values>

      <Closing>
        <ClosingInner>
          <ClosingTitle>Tu próxima historia empieza con las manos.</ClosingTitle>
          <Actions>
            <Action $primary to="/experiencias-ceramica-granollers">{t("quienes.ctaPrincipal")}<ActionArrow aria-hidden="true" /></Action>
            <Action to="/eventos">{t("quienes.ctaSecundaria")}<ActionArrow aria-hidden="true" /></Action>
          </Actions>
        </ClosingInner>
      </Closing>
    </Page>
  );
};

export default QuienesSomos;
